import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DependentSchema,
  DependentItemSchema,
  dependentSchema,
} from '@features/onboarding/schema/onboarding';
import {
  DEFAULT_DEPENDENTS_FORM,
  MAX_DEPENDENTS,
} from '@features/onboarding/constants';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { ApplicantFormItem } from '@features/onboarding/components/2-applicants-form/ApplicantFormItem';
import { ApplicantAddChildButton } from '@features/onboarding/components/2-applicants-form/ApplicantAddChildButton';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { ApplicantTagEnum } from '@features/onboarding/enums/applicant.enum';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { useUnderReviewStore } from 'src/store/under-review';
import { useAcceptanceChangeStore } from 'src/store/acceptance-change';
import { Form } from '@components/ui/form';
import { Separator } from '@components/ui/separator';
import { cn } from '@utils/index';
import { validatePhone } from '@utils/phone';
import { validateZipcode } from '@utils/zipcode';

import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { FormMessageSpan } from '@components/ui/form';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useInsuranceStore } from 'src/store/insurance';

export const ApplicantsForm = ({
  onSubmit,
  onInvalid,
  onChange,
}: FormStepProps) => {
  const { isUnderReview } = useUnderReviewStore();
  const { isApplicationAccepted } = useAcceptanceChangeStore();
  const { data, registerForm, unregisterForm } = useOnboardingStore();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showContactErrors, setShowContactErrors] = useState(false);
  const applicantRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lastAddedIndexRef = useRef<number | null>(null);
  const shouldScrollToMyselfRef = useRef<boolean>(false);
  const previousFieldsLengthRef = useRef<number>(1);
  const plans = useInsuranceStore((state) => state.plans);

  // Address & phone are managed outside this form (local state + store on the
  // Myself step). Exclude them from the form value, otherwise the form's
  // debounced onChange would persist its stale copy back and wipe the input.
  const { address: _address, phone: _phone, ...formInitData } = data;
  const form = useForm<DependentSchema>({
    // @ts-ignore
    resolver: zodResolver(dependentSchema),
    defaultValues: {
      applicants: [DEFAULT_DEPENDENTS_FORM],
      ...formInitData,
    },
  });

  const {
    watch,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    const subscription = watch((value) => {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        onChange(value as OnboardingSchema);
      }, 300);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timerId);
    };
  }, [watch, onChange]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'applicants',
  });

  useEffect(() => {
    previousFieldsLengthRef.current = fields.length;
  }, []);

  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  const smoothScrollTo = useCallback((element: HTMLElement, duration: number = 800) => {
    const isLargeScreen = window.innerWidth >= 1280;
    const scrollContainer = isLargeScreen ? document.getElementById('childrenBlock') : null;
    
    if (!scrollContainer) {
      const rect = element.getBoundingClientRect();
      const target = window.pageYOffset + rect.top - 100;
      const start = window.pageYOffset;
      const distance = target - start;
      
      if (Math.abs(distance) < 5) return;
      
      let startTime: number | null = null;
      const animation = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const easeProgress = easeInOutCubic(progress);
        
        window.scrollTo(0, start + distance * easeProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animation);
        }
      };
      requestAnimationFrame(animation);
      return;
    }
    
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const relativeTop = elementRect.top - containerRect.top;
    const start = scrollContainer.scrollTop;
    const target = start + relativeTop - 100;
    const distance = target - start;
    
    if (Math.abs(distance) < 5) {
      return;
    }
    
    let startTime: number | null = null;
    
    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easeProgress = easeInOutCubic(progress);
      
      scrollContainer.scrollTop = start + distance * easeProgress;
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }, []);

  useEffect(() => {
    registerForm('applicantsForm', (resetData) => {
      const { address: _a, phone: _p, ...rest } = resetData;
      form.reset(rest);
    });

    return () => {
      unregisterForm('applicantsForm');
    };
  }, [form, registerForm, unregisterForm]);

  const handleAddDependent = (
    e: React.MouseEvent<HTMLButtonElement>,
    applicant: 'dependent' | 'spouse'
  ) => {
    e.preventDefault();
    if (
      fields.filter((e) => e.applicant === 'dependent').length >= MAX_DEPENDENTS
    ) {
      setErrorMessage(
        `Sorry, the maximum number of child dependents is ${MAX_DEPENDENTS}.`
      );
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }
    lastAddedIndexRef.current = fields.length;
    append({
      ...DEFAULT_DEPENDENTS_FORM,
      applicant,
    } as DependentItemSchema);
  };

  useEffect(() => {
    const currentLength = fields.length;
    const previousLength = previousFieldsLengthRef.current;
    const isAdding = currentLength > previousLength;
    const isRemoving = currentLength < previousLength;

    if (
      isAdding &&
      lastAddedIndexRef.current !== null &&
      !shouldScrollToMyselfRef.current &&
      applicantRefs.current[lastAddedIndexRef.current]
    ) {
      const isSmallScreen = window.innerWidth < 1200;
      const delay = isSmallScreen ? 250 : 150;

      const scrollTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const targetElement =
              applicantRefs.current[lastAddedIndexRef.current!];
            if (targetElement) {
              targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
              });
            }
            lastAddedIndexRef.current = null;
          });
        });
      }, delay);

      previousFieldsLengthRef.current = currentLength;
      return () => clearTimeout(scrollTimer);
    }

    if (
      isRemoving &&
      shouldScrollToMyselfRef.current &&
      lastAddedIndexRef.current === null
    ) {
      const isLargeScreen = window.innerWidth >= 1280;
      const delay = isLargeScreen ? 200 : 50;
      
      const scrollTimer = setTimeout(() => {
        const myselfElement = applicantRefs.current[0];
        if (myselfElement) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              smoothScrollTo(myselfElement, 600);
            });
          });
        }
        shouldScrollToMyselfRef.current = false;
      }, delay);

      previousFieldsLengthRef.current = currentLength;
      return () => clearTimeout(scrollTimer);
    }

    previousFieldsLengthRef.current = currentLength;
  }, [fields.length, smoothScrollTo]);

  const handleRemoveDependent = (index: number) => {
    lastAddedIndexRef.current = null;
    shouldScrollToMyselfRef.current = true;
    remove(index);
    setErrorMessage(null);
  };

  const hasFemaleApplicant = (applicants?: Partial<DependentItemSchema>[]) =>
    applicants?.some(
      (applicant) =>
        typeof applicant.gender === 'string' &&
        applicant.gender.toLowerCase() === 'female'
    ) || false;

  return (
    <OnboardingFormStepLayout
      handleFormSubmit={async (e?: React.BaseSyntheticEvent) => {
        // Run ALL client-side validation first; the API request must only
        // happen when everything passes (RHF fields + zip/phone outside RHF).
        let rhfValid = false;
        let validData: Partial<OnboardingSchema> | undefined;
        await form.handleSubmit(
          (data) => {
            rhfValid = true;
            validData = data;
          },
          (errors) => {
            onInvalid(errors);
          }
        )(e);

        const storeData = useOnboardingStore.getState().data;
        const zipOk = validateZipcode(storeData.address?.zipcode);
        const phoneOk = validatePhone(storeData.phone);
        if (!zipOk || !phoneOk) {
          setShowContactErrors(true);
        }

        // Any validation failed -> stop here, do NOT send the request.
        if (!rhfValid || !zipOk || !phoneOk) {
          const err = new Error('CONTACT_VALIDATION_FAILED');
          (err as Error & { code: string }).code = 'CONTACT_VALIDATION_FAILED';
          throw err;
        }

        const nextHasFemaleApplicant = hasFemaleApplicant(validData?.applicants);
        const nextData = validData as Partial<OnboardingSchema>;

        if (nextHasFemaleApplicant) {
          (nextData as Record<string, unknown>).clearPregnancyOnNextPHQ = true;
          nextData.healthQuestions = {
            ...storeData.healthQuestions,
            initialQuestions: {
              ...storeData.healthQuestions?.initialQuestions,
              pregnancy: undefined,
            },
          };
        } else if (!nextHasFemaleApplicant) {
          nextData.healthQuestions = {
            ...storeData.healthQuestions,
            initialQuestions: {
              ...storeData.healthQuestions?.initialQuestions,
              pregnancy: false,
            },
          };
        }

        return onSubmit(nextData);
      }}
      isFormValid={form.formState.isValid}
    >
      <div className="w-full lg:max-w-[95%]">
        <Form {...form}>
          <form className="flex flex-col gap-y-6">
            {fields.map((field, index) => {
              const dependentNumber =
                field.applicant === ApplicantTagEnum.CHILD
                  ? fields.filter(
                      (f, idx) =>
                        f.applicant === ApplicantTagEnum.CHILD && idx <= index
                    ).length
                  : undefined;

              return (
                <ApplicantFormItem
                  key={field.id}
                  field={field}
                  index={index}
                  applicant={field.applicant}
                  totalDependents={fields.length}
                  dependentNumber={dependentNumber}
                  // @ts-ignore
                  form={form}
                  scrollRef={(el) => (applicantRefs.current[index] = el)}
                  remove={handleRemoveDependent}
                  showContactErrors={showContactErrors}
                />
              );
            })}
            <div className={cn('mt-6 bg-transparent')}>
              {errorMessage && (
                <div className="-mt-3 mb-3">
                  <FormMessageSpan text={errorMessage} />
                </div>
              )}
              <p className="pb-4 text-lg xl:text-[22px] font-bold">
                Who else would you like to cover?
              </p>
              <Separator className="my-0" />
              {!fields.some(
                (field) => field.applicant === ApplicantTagEnum.SPOUSE
              ) && (
                <>
                  <ApplicantAddChildButton
                    title={'Add a Spouse'}
                    onClick={(e) =>
                      handleAddDependent(e, ApplicantTagEnum.SPOUSE)
                    }
                  />
                  <Separator className="my-0" />
                </>
              )}
              <ApplicantAddChildButton
                title="Add a Child or Dependent"
                onClick={(e) => handleAddDependent(e, ApplicantTagEnum.CHILD)}
              />
              <Separator className="my-0" />
            </div>
          </form>
        </Form>
      </div>
    </OnboardingFormStepLayout>
  );
};
